import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, addMonths, subMonths, isBefore, isEqual, isValid, isSameDay, isAfter } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, DollarSign, Receipt, Trash2, AlertCircle, Circle, Repeat, Edit2, CreditCard, Zap, CheckCircle, Calendar as CalendarIcon, FileDown, Wallet } from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { CalendarEvent } from '../types';
import EventModal from './EventModal';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import BalanceAdjustmentModal from './BalanceAdjustmentModal';
import useEvents from '../hooks/useEvents';
import { updateEvent } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { doc, getDoc, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import BalanceReconciliation from './BalanceReconciliation';
import BillExport from './BillExport';

interface DayCell {
  date: Date;
  events: CalendarEvent[];
  isCurrentMonth: boolean;
  runningBalance: number;
}

const sortDayEvents = (events: CalendarEvent[]): CalendarEvent[] => {
  return events.sort((a, b) => {
    const typeOrder = { payday: 0, bill: 1, bucket: 2, adjustment: 3 };
    const typeComparison = typeOrder[a.type] - typeOrder[b.type];
    
    if (typeComparison !== 0) return typeComparison;
    
    return b.amount - a.amount;
  });
};

export default function Calendar() {
  const { user } = useAuth();
  const { events, loading, error, deleteEvent } = useEvents();
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalEventType, setModalEventType] = React.useState<'payday' | 'bill' | 'adjustment' | 'bucket' | null>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<CalendarEvent | null>(null);
  const [eventToDelete, setEventToDelete] = React.useState<{ id: string; isRecurring: boolean; title: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isBalanceModalOpen, setIsBalanceModalOpen] = React.useState(false);
  const [selectedBalance, setSelectedBalance] = React.useState<{ date: Date; balance: number } | null>(null);
  const [updateError, setUpdateError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!loading && events !== null) {
      setIsInitialized(true);
    }
  }, [loading, events]);

  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const calculateRunningBalance = React.useCallback((date: Date): number => {
    if (!events || !isValid(date)) return 0;

    const latestAdjustment = events
      .filter(event => 
        event.type === 'adjustment' && 
        (isBefore(event.date, date) || isSameDay(event.date, date))
      )
      .sort((a, b) => b.date.getTime() - a.date.getTime())[0];

    const startDate = latestAdjustment ? latestAdjustment.date : new Date(0);
    const startingBalance = latestAdjustment ? latestAdjustment.amount : 0;

    const relevantTransactions = events
      .filter(event => {
        const eventDate = event.date;
        return (isAfter(eventDate, startDate) || (event === latestAdjustment)) &&
               (isBefore(eventDate, date) || isSameDay(eventDate, date));
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return relevantTransactions.reduce((balance, event) => {
      if (event.type === 'adjustment') {
        return event.amount;
      }
      if (event.type === 'payday') {
        return balance + event.amount;
      }
      if (event.type === 'bill' || event.type === 'bucket') {
        return balance - event.amount;
      }
      return balance;
    }, startingBalance);
  }, [events]);

  const getDaysInMonth = React.useCallback((date: Date): DayCell[] => {
    const start = startOfWeek(startOfMonth(date));
    const end = endOfWeek(endOfMonth(date));
    
    return eachDayOfInterval({ start, end }).map(day => ({
      date: day,
      events: sortDayEvents(
        events?.filter(event => 
          format(event.date, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')
        ) || []
      ),
      isCurrentMonth: isSameMonth(day, date),
      runningBalance: calculateRunningBalance(day)
    }));
  }, [events, calculateRunningBalance]);

  const handleEventTypeSelect = (type: 'payday' | 'bill' | 'adjustment' | 'bucket') => {
    if (!user) {
      setUpdateError('You must be logged in to add transactions');
      return;
    }
    
    if (selectedDate) {
      setModalEventType(type);
    } else {
      setSelectedDate(new Date());
      setModalEventType(type);
    }
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleEditEvent = async (e: React.MouseEvent, event: CalendarEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      setUpdateError('You must be logged in to edit transactions');
      return;
    }

    setSelectedDate(event.date);
    setModalEventType(event.type);
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleTogglePaid = async (e: React.MouseEvent, event: CalendarEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      setUpdateError('You must be logged in to update transactions');
      return;
    }

    try {
      const updatedEvent = {
        ...event,
        isPaid: !event.isPaid,
        paidDate: !event.isPaid ? new Date() : undefined
      };

      // Handle debt payments
      if (event.debtId && !event.isPaid) {
        const debtRef = doc(db, 'debts', event.debtId);
        const debtDoc = await getDoc(debtRef);
        
        if (debtDoc.exists()) {
          await updateDoc(debtRef, {
            totalPaid: increment(event.amount),
            balance: increment(-event.amount),
            updatedAt: new Date()
          });
        }
      }
      else if (event.debtId && event.isPaid) {
        const debtRef = doc(db, 'debts', event.debtId);
        const debtDoc = await getDoc(debtRef);
        
        if (debtDoc.exists()) {
          await updateDoc(debtRef, {
            totalPaid: increment(-event.amount),
            balance: increment(event.amount),
            updatedAt: new Date()
          });
        }
      }
      // Handle budget bucket funding
      else if (event.type === 'bucket' && event.bucketId) {
        const bucketRef = doc(db, 'budgetBuckets', event.bucketId);
        const bucketDoc = await getDoc(bucketRef);
        
        if (bucketDoc.exists()) {
          const currentData = bucketDoc.data();
          
          if (!event.isPaid) {
            // Adding funds when marking as paid
            const transaction = {
              id: crypto.randomUUID(),
              amount: event.amount,
              description: 'Added Funds',
              date: Timestamp.now()
            };

            await updateDoc(bucketRef, {
              initialAmount: (currentData.initialAmount || 0) + event.amount,
              remainingAmount: (currentData.remainingAmount || 0) + event.amount,
              transactions: [...(currentData.transactions || []), transaction],
              updatedAt: Timestamp.now()
            });
          } else {
            // Removing funds when unmarking as paid
            const transaction = {
              id: crypto.randomUUID(),
              amount: -event.amount,
              description: 'Removed Funds',
              date: Timestamp.now()
            };

            await updateDoc(bucketRef, {
              initialAmount: Math.max(0, (currentData.initialAmount || 0) - event.amount),
              remainingAmount: Math.max(0, (currentData.remainingAmount || 0) - event.amount),
              transactions: [...(currentData.transactions || []), transaction],
              updatedAt: Timestamp.now()
            });
          }
        }
      }

      await updateEvent(updatedEvent);
      setUpdateError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update payment status';
      console.error('Error updating payment status:', message);
      setUpdateError(message);
    }
  };

  const handleDeleteEvent = async (e: React.MouseEvent, eventId: string, isRecurring: boolean, title: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    setEventToDelete({ id: eventId, isRecurring, title });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async (deleteAllFuture: boolean) => {
    if (!eventToDelete || !events) return;
    
    try {
      const eventToRemove = events.find(e => e.id === eventToDelete.id);
      if (!eventToRemove) return;

      if (deleteAllFuture && eventToRemove.isRecurring && eventToRemove.recurringGroupId) {
        const recurringEvents = events.filter(e => 
          e.recurringGroupId === eventToRemove.recurringGroupId &&
          (isAfter(e.date, eventToRemove.date) || isSameDay(e.date, eventToRemove.date))
        );

        for (const event of recurringEvents) {
          await deleteEvent(event.id);
        }
      } else {
        await deleteEvent(eventToDelete.id);
      }

      setEventToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const days = React.useMemo(() => getDaysInMonth(currentDate), [currentDate, getDaysInMonth]);

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setModalEventType('bucket');
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleBalanceClick = (date: Date, balance: number) => {
    setSelectedBalance({ date, balance });
    setIsBalanceModalOpen(true);
  };

  const handleEventClick = (e: React.MouseEvent, event: CalendarEvent) => {
    e.stopPropagation();
    setSelectedDate(event.date);
    setModalEventType(event.type);
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const updateSavingsAmount = async (paydayEvent: CalendarEvent) => {
    if (!events) return;
    
    const savingsEvent = events.find(event => 
      event.type === 'bill' &&
      event.title === 'Savings' &&
      format(event.date, 'yyyy-MM-dd') === format(paydayEvent.date, 'yyyy-MM-dd')
    );

    if (savingsEvent) {
      const newSavingsAmount = (paydayEvent.amount * (paydayEvent.savingsPercentage || 0)) / 100;
      await updateEvent({
        ...savingsEvent,
        amount: newSavingsAmount
      });
    }
  };

  const handleEventUpdate = async (updatedEvent: CalendarEvent) => {
    try {
      await updateEvent(updatedEvent);
      
      if (updatedEvent.type === 'payday') {
        await updateSavingsAmount(updatedEvent);
      }
      
      setIsModalOpen(false);
      setModalEventType(null);
      setSelectedDate(null);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error updating event:', error);
      setUpdateError('Failed to update event. Please try again.');
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const usableWidth = pageWidth - (margin * 2);
    const usableHeight = pageHeight - (margin * 2);

    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    const title = format(currentDate, 'MMMM yyyy');
    doc.text(title, pageWidth / 2, margin + 8, { align: 'center' });

    doc.setFontSize(10);
    const summaryY = margin + 18;
    doc.text(`Monthly Income: $${monthlyIncome.toFixed(2)}`, margin, summaryY);
    doc.text(`Monthly Expenses: $${monthlyExpenses.toFixed(2)}`, margin + 60, summaryY);
    doc.text(`End of Month Balance: $${endOfMonthBalance.toFixed(2)}`, margin + 120, summaryY);

    const startY = summaryY + 10;
    const weeksInMonth = Math.ceil(days.length / 7);
    
    const cellWidth = usableWidth / 7;
    const cellHeight = (usableHeight - 30) / weeksInMonth;

    doc.setFillColor(240, 240, 240);
    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach((day, i) => {
      doc.rect(margin + (i * cellWidth), startY, cellWidth, 7, 'F');
      doc.text(day, margin + (i * cellWidth) + (cellWidth / 2), startY + 5, { align: 'center' });
    });

    let currentY = startY + 7;
    days.forEach((day, index) => {
      const col = index % 7;
      const row = Math.floor(index / 7);
      const x = margin + (col * cellWidth);
      const y = currentY + (row * cellHeight);

      doc.setDrawColor(200);
      doc.rect(x, y, cellWidth, cellHeight);

      doc.setFontSize(10);
      doc.setTextColor(day.isCurrentMonth ? 0 : 150);
      doc.text(format(day.date, 'd'), x + 2, y + 5);
      
      doc.setFontSize(8);
      const balance = day.runningBalance;
      doc.setTextColor(balance >= 0 ? 0 : 255, 0, 0);
      doc.text(`Balance: $${balance.toFixed(2)}`, x + 2, y + 10);

      doc.setFontSize(8);
      let eventY = y + 15;
      day.events.forEach(event => {
        if (eventY > y + cellHeight - 4) return;

        switch (event.type) {
          case 'payday':
            doc.setTextColor(39, 174, 96);
            break;
          case 'bill':
            doc.setTextColor(231, 76, 60);
            break;
          case 'adjustment':
            doc.setTextColor(142, 68, 173);
            break;
        }

        const title = event.title.length > 15 ? event.title.substring(0, 15) + '...' : event.title;
        const amount = event.amount.toFixed(2);
        doc.text(`${title}: $${amount}`, x + 2, eventY);
        eventY += 4;
      });
    });

    doc.save(`calendar-${format(currentDate, 'yyyy-MM')}.pdf`);
  };

  if (loading || !isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-red-600">
        Error: {error.message}
      </div>
    );
  }

  const monthlyIncome = events
    .filter(event => 
      event.type === 'payday' && 
      isSameMonth(event.date, currentDate)
    )
    .reduce((sum, event) => sum + event.amount, 0);

  const monthlyExpenses = events
    .filter(event => 
      event.type === 'bill' && 
      isSameMonth(event.date, currentDate)
    )
    .reduce((sum, event) => sum + event.amount, 0);

  const lastDayOfMonth = endOfMonth(currentDate);
  const endOfMonthBalance = calculateRunningBalance(lastDayOfMonth);

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Add BalanceReconciliation component */}
        <BalanceReconciliation />

        {/* Rest of the Calendar component remains the same */}
        <div className="glass-effect rounded-2xl overflow-hidden animate-fadeIn">
          {updateError && (
            <div className="p-4 bg-red-50 border-l-4 border-red-500">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-700">{updateError}</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-6 border-b border-slate-200/50 bg-white">
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePrevMonth}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <h2 className="text-2xl font-bold text-slate-800">
                {format(currentDate, 'MMMM yyyy')}
              </h2>
              <button
                onClick={handleNextMonth}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            
            <div className="flex items-center space-x-4">
              <BillExport currentMonth={currentDate} />
              <button
                onClick={handleExportPDF}
                className="flex items-center px-4 py-2 glass-effect text-slate-700 rounded-lg hover:scale-105 transition-all duration-300"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export PDF
              </button>
              <button
                onClick={() => handleEventTypeSelect('payday')}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors mr-2"
                title="Add a payday to enable budget buckets"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Add Payday
              </button>
              <button
                onClick={() => handleEventTypeSelect('bill')}
                className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Add Bill
              </button>
              <button
                onClick={() => handleEventTypeSelect('bucket')}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Add Bucket
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 p-6 bg-slate-50">
            <div className="p-4 bg-white rounded-lg shadow-sm border border-slate-200">
              <h3 className="text-sm font-medium text-slate-600 mb-2">Monthly Income</h3>
              <p className="text-2xl font-bold text-green-600">${monthlyIncome.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm border border-slate-200">
              <h3 className="text-sm font-medium text-slate-600 mb-2">Monthly Expenses</h3>
              <p className="text-2xl font-bold text-red-600">${monthlyExpenses.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-white rounded-lg shadow-sm border border-slate-200">
              <h3 className="text-sm font-medium text-slate-600 mb-2">End of Month Balance</h3>
              <p className={`text-2xl font-bold ${endOfMonthBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${endOfMonthBalance.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 bg-white p-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="bg-slate-100 p-3 text-center text-sm font-semibold text-slate-700">
                {day}
              </div>
            ))}
            
            {days.map((day, idx) => (
              <div
                key={idx}
                onClick={() => handleDateClick(day.date)}
                className={`min-h-[200px] p-3 ${
                  day.isCurrentMonth ? 'bg-white hover:bg-slate-50' : 'bg-slate-50'
                } cursor-pointer transition-colors relative group border border-slate-200`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-semibold ${
                    day.isCurrentMonth
                      ? 'text-slate-700'
                      : 'text-slate-400'
                  }`}>
                    {format(day.date, 'd')}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBalanceClick(day.date, day.runningBalance);
                    }}
                    className={`text-xs font-semibold px-2 py-1 rounded transition-colors ${
                      day.runningBalance >= 0
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    ${day.runningBalance.toFixed(2)}
                  </button>
                </div>
                
                <div className="space-y-1">
                  {day.events.map((event) => (
                    <div
                      key={event.id}
                      onClick={(e) => handleEventClick(e, event)}
                      className={`text-xs px-2 py-1.5 rounded group relative flex items-center justify-between transition-colors cursor-pointer ${
                        event.type === 'payday'
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : event.type === 'adjustment'
                          ? 'bg-purple-600 hover:bg-purple-700 text-white'
                          : event.type === 'bucket'
                          ? event.isPaid ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                          : event.isPaid ? 'bg-green-500 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                      title={`
                        ${event.title}
                        Amount: $${event.amount.toFixed(2)}
                        ${event.isPaid ? '✓ Paid' : ''}
                        ${event.isAutopay ? '⚡ Autopay' : ''}
                        ${event.isScheduled ? '📅 Scheduled' : ''}
                        ${event.isSubscription ? '💳 Subscription' : ''}
                        ${event.isRecurring ? '🔄 Recurring' : ''}
                      `}
                    >
                      <div className="flex items-center space-x-1 min-w-0">
                        {(event.type === 'bill' || event.type === 'bucket') && event.isPaid && (
                          <CheckCircle className="w-3 h-3 flex-shrink-0 text-green-500" />
                        )}
                        {event.isAutopay && <Zap className="w-3 h-3 flex-shrink-0 text-blue-500" />}
                        {event.isScheduled && <CalendarIcon className="w-3 h-3 flex-shrink-0 text-orange-500" />}
                        {event.isSubscription && <CreditCard className="w-3 h-3 flex-shrink-0 text-purple-500" />}
                        <span className="truncate font-medium">{event.title}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="flex-shrink-0 font-medium">${event.amount.toFixed(2)}</span>
                        <div className="hidden group-hover:flex items-center space-x-0.5 ml-1 bg-white/90 rounded">
                          {(event.type === 'bill' || event.type === 'bucket') && (
                            <button
                              onClick={(e) => handleTogglePaid(e, event)}
                              className={`p-0.5 rounded ${
                                event.isPaid
                                  ? 'text-green-600 hover:bg-green-100'
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={event.isPaid ? 'Mark as Unpaid' : 'Mark as Paid'}
                            >
                              <CheckCircle className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleEditEvent(e, event)}
                            className="p-0.5 rounded text-gray-400 hover:bg-gray-100"
                            title="Edit"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteEvent(e, event.id, event.isRecurring || false, event.title)}
                            className="p-0.5 rounded text-gray-400 hover:bg-gray-100"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {deleteDialogOpen && eventToDelete && (
        <DeleteConfirmDialog
          isOpen={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setEventToDelete(null);
          }}
          onConfirm={confirmDelete}
          title={eventToDelete.isRecurring ? "Delete Recurring Event" : "Delete Event"}
          message={
            eventToDelete.isRecurring
              ? `Are you sure you want to delete "${eventToDelete.title}"?`
              : `Are you sure you want to delete "${eventToDelete.title}"? This action cannot be undone.`
          }
          showRecurringOption={eventToDelete.isRecurring}
        />
      )}

      {isModalOpen && (
        <EventModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setModalEventType(null);
            setSelectedDate(null);
            setSelectedEvent(null);
          }}
          date={selectedDate}
          eventType={modalEventType}
          onEventTypeChange={setModalEventType}
          event={selectedEvent}
          events={events}
          onEventUpdate={handleEventUpdate}
        />
      )}

      {isBalanceModalOpen && selectedBalance && (
        <BalanceAdjustmentModal
          isOpen={isBalanceModalOpen}
          onClose={() => {
            setIsBalanceModalOpen(false);
            setSelectedBalance(null);
          }}
          date={selectedBalance.date}
          currentBalance={selectedBalance.balance}
        />
      )}
    </div>
  );
}